
    import { Component, Provide, Vue, Prop, Watch, Emit } from 'vue-property-decorator';
    import store from '@/store'
    import { ComponentBase } from '@/models/ComponentBase';
    import { Product } from '@/models/Product';
    import { ProductLookupData } from '@/models/ProductLookupData';
    import { FormStateType } from '@/models/FormStateType';
    import { IGetItem } from '@/interfaces/IGetItem';
    import { DropdownOption } from '@/models/DropdownOption';

    @Component
    export default class ProductsForm extends ComponentBase implements IGetItem {

        @Prop()
        public productId: number;
        @Prop()
        public formState: FormStateType;
        public numberList: Array<number> = [];

        public designBoardApproverStatuses: Array<DropdownOption> = [
            {
                id: 'Approved', text: 'Approved'
            },
            {
                id: 'Under Review', text: 'Under Review'
            },
            {
                id: 'Rejected', text: 'Rejected'
            },       
            {
                id: 'Pending', text: 'Pending'
            },       
            {
                id: 'UNDEFINED', text: 'UNDEFINED'
            }
        ];

        public disasterRecoveryClasses: Array<DropdownOption> = [
            {
                id: 'DR1', text: 'DR1'
            },
            {
                id: 'DR2', text: 'DR2'
            },
            {
                id: 'DR3', text: 'DR3'
            },       
            {
                id: 'DR4', text: 'DR4'
            },    
            {
                id: 'DR5', text: 'DR5'
            },        
            {
                id: 'UNDEFINED', text: 'UNDEFINED'
            }
        ];

        // The editedItem object is initialised here so that vue-property-decorator makes it an observable @data property
        // any reassigning of whole object needs to be done like this: Object.assign({}, x); so that the observable object is not replaced
        public editedItem: Product = new Product();

        public lookupData: ProductLookupData = new ProductLookupData();
        protected apiUrlBase = "/api/products/";
        protected overlay = false;
        private editItemIsValid = true;
        private deliveryDateExpectedMenu = false;
        private deliveryDateCommittedMenu = false;
        private isNameExisting = false;
        public geoLookupData: Array<DropdownOption> = [];

        public async getItem(id: number) {
            if (this.formState == FormStateType.Edit) {
                this.showOverlay();
                await this.$axios.get<Product>(this.apiUrlBase + id)
                    .then(response => {
                        this.$nextTick(() => {
                            this.editedItem = Object.assign({}, response.data);
                            this.setProductName(this.editedItem.productName + " [" + this.editedItem.productId + "]");
                            this.setLoadStamp(this.editedItem.loadFor + " (" + this.editedItem.loadDateAsText + " UTC)");
                            this.getGeoLookupData(this.editedItem.geoCoverageLevelIds);
                            this.setLoadBy(this.editedItem.loadBy);
                            this.setModelPublished(this.editedItem.modelPublished);
                        });
                    })
                    .catch(err => {
                        this.showErrorMessage(err, "Could not get item");
                    }).finally(() => {
                        this.hideOverlay();
                    });;
            }

        }

        protected async getLookupData() {
            await this.$axios.get<ProductLookupData>(this.apiUrlBase + "lookup-data")
                .then(response => {
                    this.lookupData = Object.assign({}, response.data);
                })
                .catch(err => {
                    this.showErrorMessage(err, "Could not get lookup data");
                });
        }


        private async create() {
            this.showOverlay();

            if (this.editItemIsValid) {
                this.getNameExists().then(() => {
                    if (!this.isNameExisting) {
                        this.$axios.post(this.apiUrlBase, this.editedItem)
                            .then(response => {
                                this.$router.push({ path: '/products/' }).then(() => {
                                    this.showResponseFeedback(response);
                                });
                            })
                            .catch(err => {
                                this.showErrorMessage(err);

                            }).finally(() => {
                                this.hideOverlay();
                            });
                    } else {
                        this.hideOverlay();
                    }
                });
            }

        }

        private async save() {
            this.showOverlay();

            if (this.editItemIsValid) {
                this.getNameExists().then(() => {
                    if (!this.isNameExisting) {
                        this.$axios.put(this.apiUrlBase + this.editedItem.productId, this.editedItem)
                            .then(response => {
                                this.$router.push({ path: '/products/' }).then(() => {
                                    this.showResponseFeedback(response);
                                });
                            })
                            .catch(err => {
                                this.showErrorMessage(err);
                            }).finally(() => {
                                this.hideOverlay();
                            });
                    } else {
                        this.hideOverlay();
                    }
                });
            }

        }

        private async getNameExists() {
            await this.$axios.get<boolean>(this.apiUrlBase + "name-exists", {
                params: { productId: this.productId, productName: this.editedItem.productName, platformId: this.editedItem.platformId }
            })
            .then(response => {
                this.isNameExisting = response.data;
            })
            .catch(err => {
                this.showErrorMessage(err, "Could not get existing name status");
            });
        }

        protected showOverlay() {
            this.overlay = true;
        }

        protected hideOverlay() {
            this.overlay = false;
        }

        // used to set the product name in the parent component
        private setProductName(value: string) {
            return this.$emit("setProductName", value);
        }

        private setLoadBy(value: string) {
            return this.$emit("setLoadBy", value);
        }

        private setModelPublished(value: boolean) {
            return this.$emit("setModelPublished", value);
        }

        // used to set the load date text in the parent component
        private setLoadStamp(value: string) {
            return this.$emit("setLoadStamp", value);
        }

        private async getGeoLookupData(geoCoverageLevelIds: string) {
            if (geoCoverageLevelIds) {
                await this.$axios.get<Array<DropdownOption>>("/api/lookup/geo-coverage/" + geoCoverageLevelIds).then(response => {
                    this.geoLookupData = response.data;
                }).catch(err => {
                    this.showErrorMessage(err, "Could not get lookup data");
                    return Promise.reject(err);
                });
            }
        }     

        get isCreateState() {
            return this.formState == FormStateType.Create;
        }

        get isEditState() {
            return this.formState == FormStateType.Edit;
        }

        created() {
            for (let i = 0; i <= 9999; i++) { this.numberList.push(i); }
            this.lookupData = new ProductLookupData();
            this.showOverlay();
            Promise.all([this.getLookupData(), this.getItem(this.productId)])
            .finally(() => {
                this.hideOverlay();
            });
        }

        constructor() {
            super();
        }



    }
